<script>
import api from "../axios.js";
import { useI18n } from "vue-i18n";
const apiBaseUrl = process.env.VUE_APP_API_BASE_URL;

export default {
  data() {
    return {
      employee: {
        email: "",
        firstname: "",
        lastname: "",
        companyName: "",
      },
      companyInformation: {},
      meetings: [],
      groupedMeetings: {},
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      confirmDialog: false,
      selectedMeeting: null,
      selectedMeetingId: null,
      selectedMeetingBooked: false,
      bookedCount: 0,
      availableCount: 0,
      availableTimes: 0,
      reasonDialog: false,
      isLoading: false,
    };
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  created() {
    this.fetchAllCompanyData();
  },
  methods: {
    async fetchAllCompanyData() {
      try {
        const employeeRes = await api.get(`${apiBaseUrl}/employee`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        this.employee = employeeRes.data;

        const companyId = this.employee.company;
        if (!companyId) {
          this.showNotification(this.t("errors.missingCompanyId"), "error");
          return;
        }

        const companyAppliedTimesRes = await api.get(
          `${apiBaseUrl}/contact-meetings/company/${companyId}/2026-11-10`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        this.meetings = companyAppliedTimesRes.data;

        const companyData = await api.get(
          `${apiBaseUrl}/companies/company/${companyId}`,
          {
            companyId,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        this.companyInformation = companyData.data;

        this.availableTimes = this.companyInformation.availableTimes.length;

        // Räkna bokade och obokade möten
        this.bookedCount = this.meetings.filter(
          (m) => m.booked === true,
        ).length;
        this.availableCount =
          companyData.data.availableTimes.length -
          this.meetings.filter((m) => m.booked === true).length;

        // Gruppera möten per user
        this.groupedMeetings = this.meetings.reduce((groups, meeting) => {
          if (!groups[meeting.user]) {
            groups[meeting.user] = {
              userId: meeting.user,
              firstname: meeting.firstname,
              lastname: meeting.lastname,
              meetingRoom: meeting.meetingRoom,
              email: meeting.email,
              phone: meeting.phone,
              linkedIn: meeting.linkedIn,
              cv: meeting.cv,
              school: meeting.school,
              program: meeting.program,
              graduationYear: meeting.graduationYear,
              meetings: [],
            };
          }
          groups[meeting.user].meetings.push(meeting);

          groups[meeting.user].meetings.sort((a, b) => {
            return (
              this.parseDateTime(a.date, a.time) -
              this.parseDateTime(b.date, b.time)
            );
          });

          return groups;
        }, {});
      } catch (err) {
        console.error(err);
        this.showNotification(err);
      }
    },
    async bookMeeting(meetingId, bookedValue) {
      this.isLoading = true;
      try {
        const res = await api.put(
          `${apiBaseUrl}/contact-meetings/book-meeting/`,
          { meetingId, bookedValue },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        const updatedMeeting = res.data;

        await this.fetchAllCompanyData();

        for (const person of Object.values(this.groupedMeetings)) {
          const m = person.meetings.find((m) => m._id === updatedMeeting._id);
          if (m) {
            m.booked = updatedMeeting.booked;
          }
        }

        if (bookedValue === true) {
          this.showNotification(this.t("success.cancelMeeting"), "success");
        } else if (bookedValue === false) {
          this.showNotification(this.t("success.bookMeeting"), "success");
        }
      } catch (err) {
        console.error(err);
        if (bookedValue === true) {
          this.showNotification(this.t("errors.cancelMeeting"), "error");
        } else if (bookedValue === false) {
          this.showNotification(this.t("errors.bookMeeting"), "error");
        }
      } finally {
        this.isLoading = false;
      }
    },
    async downloadCV(cv) {
      try {
        const res = await api.get(`${apiBaseUrl}/students/cv`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { path: cv.path },
        });

        window.open(res.data.url, "_blank");
      } catch (err) {
        console.error(err);
        this.showNotification("Misslyckades att hämta CV", "error");
      }
    },

    openConfirmDialog(meeting) {
      this.selectedMeeting = meeting;
      this.selectedMeetingId = meeting._id;
      this.selectedMeetingBooked = meeting.booked;
      this.confirmDialog = true;
    },
    async confirmBooking() {
      this.confirmDialog = false;
      await this.bookMeeting(
        this.selectedMeetingId,
        this.selectedMeetingBooked,
      );
    },
    openReasonDialog() {
      this.reasonDialog = true;
    },
    showNotification(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    parseDateTime(dateString, timeRange) {
      if (!dateString || !timeRange) return new Date(0);

      const [start] = timeRange.split("-"); // "13:45"
      const [hour, minute] = start.split(":").map(Number);

      const date = new Date(dateString);
      date.setHours(hour, minute, 0, 0);

      return date;
    },
  },
};
</script>

<template>
  <div class="mt-16 w-75 mx-auto">
    <v-row class="mt-2">
      <v-col cols="12" md="6">
        <h1>{{ employee.companyName }}</h1>

        <h2>{{ employee.firstname + " " + employee.lastname }}</h2>
        <h3>{{ employee.email }}</h3>
        <v-btn
          ><router-link to="/change-password">{{
            t("login.changePassword")
          }}</router-link></v-btn
        >
        <h3>
          {{ t("company.meetingRoom") + ": " + companyInformation.meetingRoom }}
        </h3>

        <h4>
          {{
            t("employee.youHave") +
            " " +
            availableCount +
            " " +
            t("employee.freeTimes")
          }}
        </h4>
        <h4>
          {{
            t("employee.youHave") +
            " " +
            bookedCount +
            " " +
            t("employee.bookedMeetings")
          }}
        </h4>
        <p
          v-for="meeting in meetings
            .filter((m) => m.booked)
            .sort(
              (a, b) =>
                parseDateTime(a.date, a.time) - parseDateTime(b.date, b.time),
            )"
          :key="meeting._id"
        >
          {{ meeting.firstname + " " + meeting.lastname }}
          {{
            meeting.time +
            " " +
            new Date(meeting.date).toLocaleDateString("sv-SE")
          }}
        </p>
      </v-col>
      <v-col cols="12" md="6">
        <h1>Information</h1>
        <p
          v-html="
            t('employee.info', {
              availableTimes: `${availableTimes}`,
            })
          "
        ></p>
        <p>
          {{ t("footer.questionContact") }}
          <a href="mailto:axel.hagerback@sofre.se">axel.hagerback@sofre.se</a>
        </p>
      </v-col>
      <v-col cols="12">
        <h2>{{ t("employee.applicants") }}</h2>
        <h3 class="text-h6">
          {{ t("employee.applicantsInfo") }}
        </h3>
        <v-expansion-panels multiple>
          <v-expansion-panel
            v-for="person in Object.values(groupedMeetings).sort((a, b) => {
              const last = a.lastname.localeCompare(b.lastname, 'sv', {
                sensitivity: 'base',
              });
              return last !== 0
                ? last
                : a.firstname.localeCompare(b.firstname, 'sv', {
                    sensitivity: 'base',
                  });
            })"
            :key="person.userId"
            class="mb-2"
          >
            <v-expansion-panel-title>
              <div>
                <strong>{{ person.firstname }} {{ person.lastname }}</strong>
                <div style="font-size: 0.9em">
                  {{ person.email }} - {{ person.phone }}
                </div>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-list>
                <v-list-item>
                  <b>
                    {{ t("profile.school") + " - " }}
                  </b>
                  <span v-if="person.school">
                    {{ t(`profile.schools.${person.school}`) }}
                  </span>
                  <span v-else>{{ t("common.notSelected") }}</span>
                  <!-- FIXA SPRÅK -->
                </v-list-item>
                <v-list-item>
                  <b>
                    {{ t("profile.program") + " - " }}
                  </b>
                  <span v-if="person.program">
                    {{ t(`profile.programs.${person.program}`) }}
                  </span>
                  <span v-else>{{ t("common.notSelected") }}</span>
                </v-list-item>
                <v-list-item>
                  <b>
                    {{ t("profile.graduationYear") + " - " }}
                  </b>
                  <span v-if="person.graduationYear">
                    {{ person.graduationYear }}
                  </span>
                  <span v-else>{{ t("common.notSelected") }}</span>
                </v-list-item>
                <v-list-item v-if="person.linkedIn">
                  <v-btn
                    color="primary"
                    :href="person.linkedIn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ t("common.goTo") }} LinkedIn
                  </v-btn>
                </v-list-item>
                <v-list-item v-if="person.cv">
                  <v-btn @click="downloadCV(person.cv)" color="primary">
                    {{ t("company.downloadCv") }}
                  </v-btn>
                </v-list-item>
              </v-list>

              <h4 class="mt-4 mb-2">{{ t("employee.meetingTimes") }}</h4>
              <v-list dense>
                <v-list-item
                  v-for="meeting in person.meetings"
                  :key="meeting._id"
                >
                  <v-list-item-title>
                    {{ meeting.time }} -
                    {{ new Date(meeting.date).toLocaleDateString("sv-SE") }}
                  </v-list-item-title>

                  <v-spacer />

                  <!-- AVBOKA -->
                  <v-btn
                    v-if="meeting.booked === true"
                    color="error"
                    @click="openConfirmDialog(meeting)"
                  >
                    {{ t("common.cancelMeeting") }}
                  </v-btn>

                  <!-- BOKA -->
                  <v-btn
                    v-else-if="
                      !person.meetings.some(
                        (m) => m.booked || meeting.pending === true,
                      )
                    "
                    color="primary"
                    @click="openConfirmDialog(meeting)"
                  >
                    {{ t("common.bookMeeting") }}
                  </v-btn>

                  <div v-else @click="openReasonDialog" class="cursor-pointer">
                    <v-icon color="grey"> mdi-information </v-icon>
                    <span class="text-grey ml-1">{{
                      t("employee.bookingUnavailable")
                    }}</span>
                  </div>
                </v-list-item>
              </v-list>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <!-- SPRÅK FIX -->
    <v-dialog v-model="reasonDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6"
          >{{ t("employee.bookingNotPossible") }}?</v-card-title
        >
        <v-card-text>
          {{ t("common.either") }}:
          <ul>
            <li>{{ t("employee.alreadyBooked") }}</li>
            <li>{{ t("employee.timeUnavailable") }}</li>
            <li>{{ t("employee.studentUnavailable") }}</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="reasonDialog = false">{{
            t("common.close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- SPRÅK FIX -->
    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          {{
            selectedMeetingBooked
              ? t("employee.confirmCancelation")
              : t("employee.confirmBooking")
          }}
        </v-card-title>

        <v-card-text v-if="selectedMeeting">
          <p>
            {{ t("employee.areYouSure") }}
            {{
              selectedMeetingBooked ? t("employee.cancel") : t("employee.book")
            }}
            {{ t("employee.meetingWith") }}
            <strong
              >{{ selectedMeeting.firstname }}
              {{ selectedMeeting.lastname }}</strong
            >
            {{ t("employee.the") }}
            <strong>{{
              new Date(selectedMeeting.date).toLocaleDateString(
                t("language.date"),
                {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                },
              )
            }}</strong>
            {{ t("employee.at") }}
            <strong>{{ selectedMeeting.time }}</strong
            >?
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">{{
            t("common.close")
          }}</v-btn>
          <v-btn
            :color="selectedMeetingBooked ? 'error' : 'primary'"
            @click="confirmBooking"
          >
            {{
              selectedMeetingBooked
                ? t("common.cancelMeeting")
                : t("common.bookMeeting")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay
      v-model="isLoading"
      persistent
      class="d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate size="40" color="primary" />
    </v-overlay>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="3000"
      location="top"
    >
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>
